import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { useNavigate } from 'react-router-dom';

/**
 * Laat de Android-terugknop terugnavigeren in plaats van de app af te sluiten.
 *
 * Android geeft de terugknop (of het veeggebaar vanaf de rand) niet zelf aan de webview
 * door: zonder listener sluit hij de app, vanaf elk scherm. Een coach die vanuit een
 * wedstrijd één stap terug wil, staat dan ineens op zijn beginscherm - en dat is precies
 * het soort ding waar een recensie in de Play Store over gaat.
 *
 * canGoBack komt van de webview zelf en telt de pushState-stappen van react-router mee.
 * Staat er niets meer in de geschiedenis, dan is de terugknop op het beginscherm bedoeld
 * om de app te verlaten, en dat is op Android het verwachte gedrag - niet iets om tegen
 * te houden.
 *
 * De route '/' stuurt met `replace` door naar /coach (zie App.tsx), dus die laat geen
 * lege stap in de geschiedenis achter waar de terugknop op zou blijven hangen.
 *
 * Op iOS en op het web vuurt backButton niet, dus daar is dit een no-op.
 */
export const useAndroidTerugknop = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    let removeListener: (() => void) | undefined;

    App.addListener('backButton', ({ canGoBack }) => {
      if (!active) return;

      if (canGoBack) {
        navigate(-1);
      } else {
        void App.exitApp();
      }
    })
      .then((handle) => {
        if (active) {
          removeListener = () => handle.remove();
        } else {
          handle.remove();
        }
      })
      .catch(() => {
        // Geen plugin (web of tests): dan is er niets om op te wachten.
      });

    return () => {
      active = false;
      removeListener?.();
    };
  }, [navigate]);
};
